import { useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { zodResolver } from "@hookform/resolvers/zod"
import { CheckCircle } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { TextArea } from "@/components/ui/input"
import { textAreaDefaultMaxLength } from "@/models/common"
import {
  SendMessageToAdminSchema,
  sendMessageToAdminSchema,
} from "@/modules/care-provider/components/edit-care-provider/schema/care-provider-schema"
import { useChCareProviderSendChangeRequestMutation } from "@/modules/care-provider/components/edit-care-provider/services/useChCareProviderSendChangeRequestMutation"

const CareProviderSendToAdminCard: React.FC<{ careProviderId?: string; permanentlyClosed: boolean }> = ({
  careProviderId,
  permanentlyClosed,
}) => {
  const { t } = useTranslation()

  const [sendChangeRequest, isSending] = useChCareProviderSendChangeRequestMutation()
  const [isSendSuccessful, setIsSendSuccessful] = useState(false)

  const form = useForm<SendMessageToAdminSchema>({
    resolver: zodResolver(sendMessageToAdminSchema),
    defaultValues: {
      message: "",
    },
    reValidateMode: "onChange",
  })

  const {
    reset,
    register,
    formState: { errors, isDirty },
  } = form

  const onSubmit = (data: SendMessageToAdminSchema) => {
    if (!careProviderId) return

    setIsSendSuccessful(false)

    sendChangeRequest({
      variables: {
        input: {
          careProviderId,
          message: data.message,
        },
      },
      onCompleted: () => {
        setIsSendSuccessful(true)
        reset()
      },
    })
  }

  const handleReset = () => {
    setIsSendSuccessful(false)
    reset()
  }

  return (
    <div className="p-4">
      <h4 className="text-on-surface mb-3 text-base font-medium">
        {t("admin.careProviders.dialog.sendToAdmin.headline")}
      </h4>
      <div className="mt-4 grid w-full gap-4">
        <div className="custom-textarea group mb-4">
          <TextArea
            disabled={permanentlyClosed}
            {...register("message")}
            onFocus={() => setIsSendSuccessful(false)}
            error={t(errors?.message?.message || "")}
            label={`${t("admin.careProviders.dialog.sendToAdmin.message")}*`}
            textLimit={textAreaDefaultMaxLength}
            data-testid="send-change-request-textarea"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.stopPropagation()
              }
            }}
          />
        </div>

        {isSendSuccessful && (
          <div className="text-body-sm grid grid-cols-1">
            <div className="text-body-sm bg-success-surface-subdued text-on-success-surface flex min-h-10 items-start gap-2 rounded-lg px-4 py-2">
              <CheckCircle />
              <div className="grid h-full items-center">
                <span className="text-inherit">{t("admin.careProviders.dialog.sendToAdmin.messageSent")}</span>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={handleReset} disabled={permanentlyClosed}>
            {t("common.confirmation.discard")}
          </Button>
          <Button onClick={form.handleSubmit(onSubmit)} isLoading={isSending} disabled={!isDirty || permanentlyClosed}>
            {t("common.button.send")}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default CareProviderSendToAdminCard
